import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { fetchPolicyAgentContact } from '../ProducerSection';
import { useCertificateHolders } from '../../../certificate-holders/hooks/useCertificateHolders';
import InsurerSearchDropdown from '../InsurerSearchDropdown';
import { formatUsPhone } from 'utilities/formatPhone';

const INSR_OPTIONS = ['A','B','C','D','E','F'].map(l => ({ label: l, value: l }));

interface GenericRow {
  code: string;
  desc: string;
  limit1: string;
  appliesTo1: string;
  limit2: string;
  appliesTo2: string;
}
const emptyRow = (): GenericRow => ({ code: '', desc: '', limit1: '', appliesTo1: '', limit2: '', appliesTo2: '' });

interface Acord20FormProps { selectedPolicies?: string[]; insuredId?: string; }

export default function Acord20Form({ selectedPolicies = [], insuredId = '' }: Acord20FormProps) {
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);

  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [producerCustomerId, setProducerCustomerId] = React.useState('');
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');
  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');
  const [insurerF, setInsurerF] = React.useState('');
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [certNumber, setCertNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');
  const [fboInsurer, setFboInsurer] = React.useState('');
  const [fboPolicyNum, setFboPolicyNum] = React.useState('');
  const [fboEffDate, setFboEffDate] = React.useState<Date | null>(null);
  const [fboExpDate, setFboExpDate] = React.useState<Date | null>(null);
  const [fboAddlInsured, setFboAddlInsured] = React.useState('');
  const [fboSubrWaived, setFboSubrWaived] = React.useState('');
  const [prodSaleFuelOil, setProdSaleFuelOil] = React.useState(false);
  const [prodExtended, setProdExtended] = React.useState(false);
  const [compOpsExtended, setCompOpsExtended] = React.useState(false);
  const [hkIncludingTaxi, setHkIncludingTaxi] = React.useState(false);
  const [hkInFlight, setHkInFlight] = React.useState(false);
  const [contractIncluded, setContractIncluded] = React.useState(false);
  const [contractExcluded, setContractExcluded] = React.useState(false);
  const [fboGenericRows, setFboGenericRows] = React.useState<GenericRow[]>(
    Array.from({ length: 6 }, emptyRow)
  );

  const [descriptionOfOps, setDescriptionOfOps] = React.useState('');
  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [holderSearchQuery, setHolderSearchQuery] = React.useState('');
  const { holders: holderSuggestions } = useCertificateHolders(holderSearchQuery, 10);
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);
  const [producerCustomerIdP2, setProducerCustomerIdP2] = React.useState('');
  const [phInsurer, setPhInsurer] = React.useState('');
  const [phPolicyNum, setPhPolicyNum] = React.useState('');
  const [phEffDate, setPhEffDate] = React.useState<Date | null>(null);
  const [phExpDate, setPhExpDate] = React.useState<Date | null>(null);
  const [phAddlInsured, setPhAddlInsured] = React.useState('');
  const [phSubrWaived, setPhSubrWaived] = React.useState('');
  const [phHkIncludingTaxi, setPhHkIncludingTaxi] = React.useState(false);
  const [phHkInFlight, setPhHkInFlight] = React.useState(false);
  const [phGenericRows, setPhGenericRows] = React.useState<GenericRow[]>(
    Array.from({ length: 2 }, emptyRow)
  );

  const [apInsurer, setApInsurer] = React.useState('');
  const [apPolicyNum, setApPolicyNum] = React.useState('');
  const [apEffDate, setApEffDate] = React.useState<Date | null>(null);
  const [apExpDate, setApExpDate] = React.useState<Date | null>(null);
  const [apAddlInsured, setApAddlInsured] = React.useState('');
  const [apSubrWaived, setApSubrWaived] = React.useState('');
  const [apInclCompOps, setApInclCompOps] = React.useState(false);
  const [apInclSpacecraft, setApInclSpacecraft] = React.useState(false);
  const [apExclCompOps, setApExclCompOps] = React.useState(false);
  const [apExclSpacecraft, setApExclSpacecraft] = React.useState(false);
  const [apForeignMilIncluded, setApForeignMilIncluded] = React.useState(false);
  const [apGenericRows, setApGenericRows] = React.useState<GenericRow[]>(
    Array.from({ length: 2 }, emptyRow)
  );

  const [otherLineOfBusiness, setOtherLineOfBusiness] = React.useState('');
  const [otherInsurer, setOtherInsurer] = React.useState('');
  const [otherPolicyNum, setOtherPolicyNum] = React.useState('');
  const [otherEffDate, setOtherEffDate] = React.useState<Date | null>(null);
  const [otherExpDate, setOtherExpDate] = React.useState<Date | null>(null);
  const [otherAddlInsured, setOtherAddlInsured] = React.useState('');
  const [otherSubrWaived, setOtherSubrWaived] = React.useState('');
  const [otherGenericRows, setOtherGenericRows] = React.useState<GenericRow[]>(
    Array.from({ length: 6 }, emptyRow)
  );

  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    let cancelled = false;
    const fetchCoverages = async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: '33C5CB7C-B487-4FAA-ADE9-8918B72EFC2E',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        await res.json();
      } catch {
        // silently fail — user can still fill manually
      }
    };
    fetchCoverages();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  // Prefill contact section from the first selected policy's agent
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    fetchPolicyAgentContact(selectedPolicies[0]).then(({ name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
    });
  }, [selectedPolicies]);

  const limitInput = (style?: React.CSSProperties) => (
    <div className="acord20__limitCell">
      <span className="acord20__limitDollar">$</span>
      <InputText className="masterCert__input" style={{ width: 70, ...style }} />
    </div>
  );

  const renderPolicyInfo = (
    insurer: string, setInsurer: (v: string) => void,
    policyNum: string, setPolicyNum: (v: string) => void,
    effDate: Date | null, setEffDate: (v: Date | null) => void,
    expDate: Date | null, setExpDate: (v: Date | null) => void,
    addlIns: string, setAddlIns: (v: string) => void,
    subrWaived: string, setSubrWaived: (v: string) => void,
  ) => (
    <>
      <div className="acord20__policyInfoHeader">
        <div style={{ textAlign: 'left' }}>INSURER LETTER</div>
        <div>POLICY NUMBER</div>
        <div>EFFECTIVE DATE</div>
        <div>EXPIRATION DATE</div>
        <div>ADDITIONAL INSURED? (Y/N)</div>
        <div>SUBROGATION WAIVED? (Y/N)</div>
      </div>
      <div className="acord20__policyInfoRow">
        <Dropdown value={insurer} onChange={(e) => setInsurer(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input masterCert__inputFull" emptyMessage="No available options" />
        <InputText value={policyNum} onChange={(e) => setPolicyNum(e.target.value)} className="masterCert__input masterCert__inputFull" />
        <div className="masterCert__dateCell">
          <Calendar value={effDate} onChange={(e) => setEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
        </div>
        <div className="masterCert__dateCell">
          <Calendar value={expDate} onChange={(e) => setExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
        </div>
        <InputText value={addlIns} onChange={(e) => setAddlIns(e.target.value)} className="masterCert__input masterCert__inputFull" />
        <InputText value={subrWaived} onChange={(e) => setSubrWaived(e.target.value)} className="masterCert__input masterCert__inputFull" />
      </div>
    </>
  );

  const renderCovHeader = () => (
    <div className="acord20__covHeader">
      <div>COVERAGE</div>
      <div>OPTIONS</div>
      <div>LIMIT</div>
      <div>APPLIES TO</div>
      <div>LIMIT</div>
      <div>APPLIES TO</div>
    </div>
  );

  const covRow = (
    name: string | React.ReactNode,
    options: React.ReactNode,
    appliesTo1: string,
    appliesTo2: string,
    isSub?: boolean,
  ) => (
    <div className={`acord20__covRow ${isSub ? 'acord20__covRow--sub' : ''}`}>
      <div className="acord20__covName">{name}</div>
      <div className="acord20__covOptions">{options}</div>
      {limitInput()}
      <div className="acord20__appliesTo">{appliesTo1}</div>
      {appliesTo2 ? limitInput() : <div />}
      <div className="acord20__appliesTo">{appliesTo2}</div>
    </div>
  );

  const renderGenericSection = (
    label: string,
    rows: GenericRow[],
    setRows: React.Dispatch<React.SetStateAction<GenericRow[]>>,
    hasSubRows?: boolean,
  ) => {
    const updateRow = (i: number, field: keyof GenericRow, value: string) => {
      setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
    };
    return (
      <>
        <div className="acord20__genericHeader">
          <div>CODE</div>
          <div>DESCRIPTION</div>
          <div>OPTIONS</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
        </div>
        {rows.map((row, i) => (
          <React.Fragment key={i}>
            <div className="acord20__genericRow">
              <InputText value={row.code} onChange={(e) => updateRow(i, 'code', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={row.desc} onChange={(e) => updateRow(i, 'desc', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <div className="acord20__covOptions">
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
              </div>
              <div className="acord20__limitCell">
                <span className="acord20__limitDollar">$</span>
                <InputText value={row.limit1} onChange={(e) => updateRow(i, 'limit1', e.target.value)} className="masterCert__input" style={{ width: 60 }} />
              </div>
              <InputText value={row.appliesTo1} onChange={(e) => updateRow(i, 'appliesTo1', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <div className="acord20__limitCell">
                <span className="acord20__limitDollar">$</span>
                <InputText value={row.limit2} onChange={(e) => updateRow(i, 'limit2', e.target.value)} className="masterCert__input" style={{ width: 60 }} />
              </div>
              <InputText value={row.appliesTo2} onChange={(e) => updateRow(i, 'appliesTo2', e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            {hasSubRows && (
              <div className="acord20__genericRow acord20__genericRow--sub">
                <div />
                <div />
                <div className="acord20__covOptions">
                  <Checkbox checked={false} />
                  <InputText className="masterCert__input" style={{ width: 80 }} />
                  <Checkbox checked={false} />
                  <InputText className="masterCert__input" style={{ width: 80 }} />
                </div>
                {limitInput({ width: 60 })}
                <InputText className="masterCert__input masterCert__inputFull" />
                {limitInput({ width: 60 })}
                <InputText className="masterCert__input masterCert__inputFull" />
              </div>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  const chk = (id: string, label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <div className="masterCert__checkboxItem">
      <Checkbox inputId={id} checked={checked} onChange={(e) => onChange(e.checked ?? false)} />
      <label htmlFor={id} className="masterCert__checkboxItemLabel">{label}</label>
    </div>
  );

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">CERTIFICATE OF AVIATION LIABILITY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This certificate is issued as a matter of information only and confers no rights upon the certificate holder. This certificate does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This certificate of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the certificate holder.
        </div>
        <div className="masterCert__disclaimerImportant">
          <span className="masterCert__importantLabel">IMPORTANT:</span> If the certificate holder is an Additional Insured, the policy(ies) must be endorsed. If subrogation is waived, subject to the terms and conditions of the policy, certain policies may require an endorsement. A statement on this certificate does not confer rights to the certificate holder in lieu of such endorsement(s).
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <ProducerSection
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
              readOnly
            />
            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <Dropdown value={insuredName} onChange={(e) => setInsuredName(e.value)} options={[]} placeholder="Type and/or choose location" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" editable disabled emptyMessage="No available options" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" readOnly />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" readOnly />
              </div>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div></div>
              </div>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
              </div>
              <div style={{ marginTop: 6 }}>
                <div className="masterCert__contactLabel">PRODUCER CUSTOMER ID #:</div>
                <InputText maxLength={30} value={producerCustomerId} onChange={(e) => setProducerCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <div className="acord20__insurerPctLabel" style={{ marginLeft: 'auto' }}>%</div>
              </div>
              <div className="acord20__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A</div>
                <InsurerSearchDropdown value={insurerA} onChange={setInsurerA} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B</div>
                <InsurerSearchDropdown value={insurerB} onChange={setInsurerB} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C</div>
                <InsurerSearchDropdown value={insurerC} onChange={setInsurerC} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D</div>
                <InsurerSearchDropdown value={insurerD} onChange={setInsurerD} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E</div>
                <InsurerSearchDropdown value={insurerE} onChange={setInsurerE} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER F</div>
                <InsurerSearchDropdown value={insurerF} onChange={setInsurerF} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer" style={{ marginTop: 12 }}>
          This is to certify that the policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this certificate may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions and conditions of such policies. Limits shown may have been reduced by paid claims.
        </div>

        <div className="acord20__sectionTitle">AIRPORT &amp; FBO LIABILITY COVERAGES</div>

        <div className="masterCert__certNumberGrid">
          <div>
            <div className="masterCert__contactLabel">CERTIFICATE NUMBER:</div>
            <InputText maxLength={30} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
          <div>
            <div className="masterCert__contactLabel">REVISION NUMBER:</div>
            <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        {renderPolicyInfo(fboInsurer, setFboInsurer, fboPolicyNum, setFboPolicyNum, fboEffDate, setFboEffDate, fboExpDate, setFboExpDate, fboAddlInsured, setFboAddlInsured, fboSubrWaived, setFboSubrWaived)}

        {renderCovHeader()}

        {covRow('PREMISES LIABILITY',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'BI EA PER', 'PD'
        )}
        {covRow('', <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>, 'EA OCC', '', true)}

        {covRow('PREMISES MEDICAL PAYMENTS',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA PER', 'EA OCC'
        )}

        {covRow('PRODUCTS LIABILITY',
          <>{chk('a20ProdFuel', 'SALE OF FUEL & OIL', prodSaleFuelOil, setProdSaleFuelOil)}<Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'BI EA PER', 'AGGR'
        )}
        {covRow('', <>{chk('a20ProdExtended', 'EXTENDED', prodExtended, setProdExtended)}</>, 'EA OCC', '', true)}

        {covRow('COMPLETED OPERATIONS LIABILITY',
          <>{chk('a20CompOpsExt', 'EXTENDED', compOpsExtended, setCompOpsExtended)}<Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'BI EA PER', 'AGGR'
        )}
        {covRow('', <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>, 'EA OCC', '', true)}

        {covRow('HANGARKEEPERS LEGAL LIABILITY',
          <>{chk('a20HkTaxi', 'INCLUDING TAXI', hkIncludingTaxi, setHkIncludingTaxi)}<Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA AIRCRAFT', 'EA OCC'
        )}
        {covRow('', <>{chk('a20HkInFlight', 'IN FLIGHT', hkInFlight, setHkInFlight)}</>, '', '', true)}

        {covRow('FIRE LEGAL LIABILITY',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'ANY ONE FIRE', ''
        )}

        {covRow('PERSONAL INJURY LIABILITY',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA OCC', 'AGGR'
        )}

        {covRow('ADVERTISING LIABILITY',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA OCC', 'AGGR'
        )}

        <div className="acord20__covRow">
          <div className="acord20__covName">CONTRACTUAL LIABILITY</div>
          <div className="acord20__covOptions">
            {chk('a20ContractIncl', 'INCLUDED', contractIncluded, setContractIncluded)}
            {chk('a20ContractExcl', 'EXCLUDED', contractExcluded, setContractExcluded)}
          </div>
          <div /><div /><div /><div />
        </div>
        {renderGenericSection('COVERAGE', fboGenericRows, setFboGenericRows)}

        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">DESCRIPTION OF OPERATIONS / REMARKS (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
          <InputTextarea maxLength={2000} value={descriptionOfOps} onChange={(e) => setDescriptionOfOps(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>
        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox">
            <div className="masterCert__sectionHeader">CERTIFICATE HOLDER</div>
            <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
              <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
              <AutoComplete
                value={holderSearchQuery}
                suggestions={holderSuggestions.map((h) => ({ label: h.name, value: h }))}
                completeMethod={(e) => setHolderSearchQuery(e.query)}
                onChange={(e) => { if (typeof e.value === 'string') setHolderSearchQuery(e.value); }}
                field="label"
                placeholder="Search for a certificate holder"
                style={{ width: '100%' }}
                inputStyle={{ width: '100%', paddingLeft: 30 }}
                disabled
                onSelect={(e) => {
                  const h = e.value.value;
                  setHolderName(h.name || '');
                  setHolderAddress(h.addressLine1 || '');
                  setHolderCity(h.city || '');
                  setHolderState(h.stateName || '');
                  setHolderZip(h.zipCode || '');
                  setHolderSearchQuery('');
                }}
              />
            </div>
            <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <div className="masterCert__addressGrid">
              <InputText placeholder="Street" className="masterCert__input masterCert__inputFull" disabled />
              <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" disabled />
              <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" disabled />
            </div>
          </div>
          <div className="masterCert__cancellationBox">
            <div className="masterCert__sectionHeader">CANCELLATION</div>
            <div className="masterCert__cancellationText">
              Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
            </div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button label="Create New" className="p-button-outlined carrierDialog__secondaryBtn" onClick={() => setShowAddSignature(true)} />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        <div className="masterCert__acordFooter">
          <div>ACORD 20 (2009/12)</div>
          <div>&copy; 1988-2014 ACORD CORPORATION. All rights reserved.</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24, marginBottom: 8 }}>
          <div className="masterCert__contactLabel" style={{ whiteSpace: 'nowrap' }}>PRODUCER CUSTOMER ID:</div>
          <InputText value={producerCustomerIdP2} onChange={(e) => setProducerCustomerIdP2(e.target.value)} className="masterCert__input" style={{ width: 300 }} />
        </div>

        <div className="acord20__sectionTitle">PRIVATE HANGAR LIABILITY COVERAGE</div>

        {renderPolicyInfo(phInsurer, setPhInsurer, phPolicyNum, setPhPolicyNum, phEffDate, setPhEffDate, phExpDate, setPhExpDate, phAddlInsured, setPhAddlInsured, phSubrWaived, setPhSubrWaived)}

        {renderCovHeader()}

        {covRow('HANGARKEEPERS LEGAL LIABILITY',
          <>{chk('a20PhHkTaxi', 'INCLUDING TAXI', phHkIncludingTaxi, setPhHkIncludingTaxi)}<Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA AIRCRAFT', 'EA OCC'
        )}
        {covRow('', <>{chk('a20PhHkInFlight', 'IN FLIGHT', phHkInFlight, setPhHkInFlight)}</>, '', '', true)}

        {renderGenericSection('COVERAGE', phGenericRows, setPhGenericRows)}

        <div className="acord20__sectionTitle">AVIATION PRODUCTS LIABILITY COVERAGES</div>

        {renderPolicyInfo(apInsurer, setApInsurer, apPolicyNum, setApPolicyNum, apEffDate, setApEffDate, apExpDate, setApExpDate, apAddlInsured, setApAddlInsured, apSubrWaived, setApSubrWaived)}

        {renderCovHeader()}

        {covRow('PRODUCTS LIABILITY',
          <>
            {chk('a20ApInclCompOps', 'INCL COMP OPS', apInclCompOps, setApInclCompOps)}
            {chk('a20ApInclSpacecraft', 'INCL SPACECRAFT', apInclSpacecraft, setApInclSpacecraft)}
          </>,
          'EA OCC', 'AGGR'
        )}
        {covRow('',
          <>
            {chk('a20ApExclCompOps', 'EXCL COMP OPS', apExclCompOps, setApExclCompOps)}
            {chk('a20ApExclSpacecraft', 'EXCL SPACECRAFT', apExclSpacecraft, setApExclSpacecraft)}
          </>,
          '', '', true
        )}

        {covRow('GROUNDING LIABILITY', <></>, 'EA OCC', 'AGGR')}

        <div className="acord20__covRow">
          <div className="acord20__covName" style={{ fontSize: '0.72rem' }}>FOREIGN MILITARY AIRCRAFT PRODUCTS</div>
          <div className="acord20__covOptions">
            {chk('a20ForeignMil', 'INCLUDED', apForeignMilIncluded, setApForeignMilIncluded)}
          </div>
          <div /><div /><div /><div />
        </div>

        {renderGenericSection('COVERAGE', apGenericRows, setApGenericRows)}

        <div className="acord20__sectionTitle">OTHER COVERAGES</div>

        <div className="acord20__lineOfBusiness">
          <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>LINE OF BUSINESS</div>
          <InputText value={otherLineOfBusiness} onChange={(e) => setOtherLineOfBusiness(e.target.value)} className="masterCert__input masterCert__inputFull" />
        </div>

        {renderPolicyInfo(otherInsurer, setOtherInsurer, otherPolicyNum, setOtherPolicyNum, otherEffDate, setOtherEffDate, otherExpDate, setOtherExpDate, otherAddlInsured, setOtherAddlInsured, otherSubrWaived, setOtherSubrWaived)}

        {renderGenericSection('COVERAGE', otherGenericRows, setOtherGenericRows, true)}

        <div className="masterCert__acordFooter">
          <div>ACORD 20 (2009/12)</div>
          <div>&copy; 1988-2014 ACORD CORPORATION. All rights reserved.</div>
        </div>
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
}
